import { clamp, cn, isDefined, render, Renderable } from "@/lib/utils";
import {
  useTable,
  useTablePosition,
  useTablePreview,
  useTableQueryResult,
  WithSetter,
} from "@/components/custom/data-table/data-table-context";
import { CircleButton } from "./circle";
import { PiArrowLeft, PiArrowRight } from "react-icons/pi";
import { RiExpandDiagonalLine, RiCollapseDiagonalLine } from "react-icons/ri";
import { LuX } from "react-icons/lu";
import { PreviewState } from "./data-table/data-table-preview";
import React from "react";
import { useCssObserver } from "@/hooks/use-css-observer";

export type PreviewNavigationProps = {
  expendable?: boolean;
  current?: number;
  lastValid?: number;
  total?: number;
  totalIsEstimate?: boolean;
  left?: () => void;
  leftDisabled?: boolean;
  right?: () => void;
  rightDisabled?: boolean;
  previewState?: WithSetter<PreviewState>;
  hideExpand?: boolean;
  onClose?: () => void;
  isLoading?: boolean;
  row?: Renderable;
  content?: React.ComponentPropsWithRef<"div">;
  onExpand?: (ev?: any) => void;
  onCollapse?: (ev?: any) => void;
} & Omit<React.ComponentProps<"div">, "content">;

const PreviewNavigation_ = (
  {
    expendable,
    current,
    lastValid,
    total,
    totalIsEstimate,
    left,
    leftDisabled,
    right,
    rightDisabled,
    previewState,
    onClose,
    hideExpand,
    row,
    children,
    className,
    content,
    onCollapse,
    onExpand,
    ...props
  }: PreviewNavigationProps,
  ref: React.ForwardedRef<HTMLDivElement>,
) => {
  const [preview, setPreview] = previewState ?? [];
  return (
    <>
      <div
        data-component="preview-navigation"
        ref={ref}
        className={cn(className, "flex gap-2 items-center")}
        {...props}
      >
        {(isDefined(current) || isDefined(lastValid)) && (
          <div>
            <span className="text-sm font-medium text-muted-foreground">
              {`${current ?? lastValid}${lastValid !== undefined ? "(?)" : ""}
                ${
                  isDefined(total)
                    ? ` of ${totalIsEstimate ? "~" : ""}${total}`
                    : ""
                }`}
            </span>
          </div>
        )}
        {(isDefined(current) || isDefined(lastValid)) && isDefined(left) && (
          <CircleButton
            data-component="preview-navigation-left"
            disabled={leftDisabled}
            onClick={left}
          >
            <PiArrowLeft />
          </CircleButton>
        )}
        {(isDefined(current) || isDefined(lastValid)) && isDefined(right) && (
          <CircleButton
            data-component="preview-navigation-right"
            disabled={rightDisabled}
            onClick={right}
          >
            <PiArrowRight />
          </CircleButton>
        )}
        <div className="flex gap-2 ml-auto items-center">
          {render(row)}
          {expendable &&
            isDefined(setPreview) &&
            !hideExpand &&
            (preview === 2 ? (
              <CircleButton
                data-component="preview-navigation-collapse"
                onClick={() => {
                  setPreview(1);
                  if (onCollapse) onCollapse();
                }}
              >
                <RiCollapseDiagonalLine />
              </CircleButton>
            ) : (
              <CircleButton
                data-component="preview-navigation-expand"
                onClick={() => {
                  setPreview(2);
                  if (onExpand) onExpand();
                }}
              >
                <RiExpandDiagonalLine />
              </CircleButton>
            ))}
          {(isDefined(setPreview) || isDefined(onClose)) && (
            <CircleButton
              data-component="preview-navigation-close"
              onClick={() => {
                if (setPreview) setPreview(0);
                if (onClose) onClose();
              }}
            >
              <LuX />
            </CircleButton>
          )}
        </div>
      </div>
      {children && (isDefined(current) || isDefined(lastValid)) ? (
        <div {...content}>{children}</div>
      ) : (
        children
      )}
    </>
  );
};

const PreviewNavigation = React.forwardRef(
  PreviewNavigation_,
) as typeof PreviewNavigation_;

export const PreviewNavigationTable = ({
  className,
  always,
  ...props
}: Omit<
  PreviewNavigationProps,
  | "expandable"
  | "current"
  | "total"
  | "left"
  | "leftDisabled"
  | "right"
  | "rightDisabled"
  | "previewState"
  | "isLoading"
> & { always?: boolean }) => {
  const table = useTable();
  const [position] = useTablePosition();
  const current = isDefined(position?.index) ? position?.index + 1 : undefined;
  const isLoading = useTableQueryResult().isLoading;
  const total = React.useMemo(
    () => table.getRowCount(),
    [table.getState().position, table.getState().pagination, isLoading],
  );
  const expandable = isDefined(position?.id) || isDefined(position?.index);
  const previewState = useTablePreview();
  const targetRef = React.useRef<HTMLDivElement | null>(null);
  const storageRef = React.useRef<HTMLDivElement | null>(null);
  useCssObserver({
    target: targetRef,
    storage: storageRef,
    mapper: { "--preview-height": "height" },
  });
  const lastValid =
    position?.lastValid?.index !== undefined
      ? position?.lastValid?.index + 1
      : undefined;
  return (
    <PreviewNavigation
      ref={targetRef}
      expendable={expandable}
      className={cn(
        !(always || isDefined(current) || isDefined(lastValid)) &&
          "absolute right-6",
        className,
      )}
      content={{
        ref: storageRef,
        className: cn(
          !(always || isDefined(current))
            ? "h-full"
            : "h-[calc(100%-var(--preview-height))]",
        ),
      }}
      current={current}
      lastValid={lastValid}
      total={total}
      left={() =>
        table.setPositionIndex(
          (p, s) =>
            p === undefined
              ? s?.lastValid?.index !== undefined
                ? clamp(s?.lastValid?.index - 1 + 1, 0)
                : p
              : clamp(p - 1, 0),
          true,
        )
      }
      leftDisabled={!table.getCanPreviousPosition()}
      right={() =>
        table.setPositionIndex(
          (p, s) =>
            p === undefined
              ? s?.lastValid?.index !== undefined
                ? clamp(
                    s?.lastValid?.index + 1 - 1,
                    undefined,
                    table.getRowCount() - 1,
                  )
                : p
              : clamp(p + 1, undefined, table.getRowCount() - 1),
          true,
        )
      }
      rightDisabled={!table.getCanNextPosition()}
      previewState={previewState}
      {...props}
    />
  );
};
